import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { SessionBadge, ZReport } from '@/features/pos/components/TillPanels'
import type { CashMovement, SessionReport } from '@/features/pos/types'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'

/**
 * Les services de caisse de la soirée : qui a tenu quoi, et ce qu'il y a
 * trouvé.
 *
 * Trois lectures, de la plus large à la plus fine :
 *
 * * **par caisse** — le bar, la cuisine, l'entrée : ce que chaque poste a
 *   encaissé, tous services confondus ;
 * * **par personne** — ce qui est passé par les mains de chacun, et l'écart
 *   qu'il a dû expliquer ;
 * * **par service** — le rapport Z complet, les mouvements ligne à ligne, et,
 *   pour une caisse restée ouverte, la fermeture par un responsable.
 *
 * Ce dernier geste manquait : un caissier parti sans compter laissait une
 * session ouverte qui bloquait la clôture de l'édition, et la seule issue
 * était de passer outre.
 *
 * Lu sur `/ops/pos/sessions` : le serveur décide qui voit quoi
 * (`pos.session.read_all`). Sans ce droit, le panneau s'efface.
 */
export function CashSessionsPanel({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState<string | null>(null)

  const query = useAdminQuery<SessionReport[]>(
    ['pos-sessions-all', eventId],
    '/ops/pos/sessions?mine=false',
    eventId,
  )

  if (query.isPending) return <Skeleton className="mt-8 h-40 w-full" />
  if (query.isError) return null

  const sessions = query.data ?? []
  const currency = sessions[0]?.currency ?? 'EUR'
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency }, i18n.language)

  const group = (key: (row: SessionReport) => string) => {
    const totals = new Map<string, { revenue: number; variance: number; count: number; open: number }>()
    for (const row of sessions) {
      const name = key(row)
      const current = totals.get(name) ?? { revenue: 0, variance: 0, count: 0, open: 0 }
      current.revenue += row.revenue_minor ?? 0
      current.variance += row.variance_minor ?? 0
      current.count += 1
      current.open += row.status === 'open' ? 1 : 0
      totals.set(name, current)
    }
    return [...totals.entries()].sort((a, b) => b[1].revenue - a[1].revenue)
  }
  const byRegister = group((row) => row.register_label)
  const byPerson = group((row) => row.user_label || '—')

  return (
    <Card className="mt-8">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.cashSessions.title')}
      </h2>
      <p className="mt-1 max-w-xl text-sm text-content-secondary">
        {t('admin.cashSessions.intro')}
      </p>

      {sessions.length === 0 ? (
        <p className="mt-5 text-sm text-content-secondary">{t('admin.cashSessions.empty')}</p>
      ) : (
        <>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <Totals title={t('admin.cashSessions.byRegister')} rows={byRegister} money={money} />
            <Totals title={t('admin.cashSessions.byPerson')} rows={byPerson} money={money} />
          </div>

          <p className="mt-6 overline text-content-muted">{t('admin.cashSessions.shifts')}</p>
          <ul className="mt-2 grid gap-2">
            {sessions.map((row) => {
              const expanded = open === row.session_id
              return (
                <li key={row.session_id} className="rounded-lg border border-line-subtle">
                  <button
                    type="button"
                    onClick={() => setOpen(expanded ? null : row.session_id)}
                    aria-expanded={expanded}
                    className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-content-primary">
                        <span className="font-mono">{row.register_label}</span> · {row.user_label}
                      </span>
                      <span className="block text-caption text-content-muted">
                        {new Date(row.opened_at).toLocaleString(i18n.language, {
                          weekday: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </span>
                    <SessionBadge status={row.status} />
                    <span className="w-full text-right tabular-nums text-content-primary sm:w-auto">
                      {money(row.revenue_minor ?? 0)}
                      {row.variance_minor ? (
                        <span
                          className={cn(
                            'ml-2 text-caption',
                            row.variance_minor < 0 ? 'text-state-danger' : 'text-ember-300',
                          )}
                        >
                          {money(row.variance_minor)}
                        </span>
                      ) : null}
                    </span>
                  </button>
                  {expanded ? (
                    <SessionDetail eventId={eventId} report={row} money={money} />
                  ) : null}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </Card>
  )
}

function Totals({
  title,
  rows,
  money,
}: {
  title: string
  rows: [string, { revenue: number; variance: number; count: number; open: number }][]
  money: (minor: number) => string
}) {
  const { t } = useTranslation()
  return (
    <div className="min-w-0">
      <p className="overline text-content-muted">{title}</p>
      <ul className="mt-2 divide-y divide-line-subtle text-sm">
        {rows.map(([name, value]) => (
          <li key={name} className="flex items-baseline justify-between gap-3 py-1.5">
            <span className="min-w-0 truncate text-content-secondary">
              {name}
              {value.open ? (
                <span className="ml-2 text-caption text-state-success">
                  {t('admin.cashSessions.openCount', { count: value.open })}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-right tabular-nums text-content-primary">
              {money(value.revenue)}
              {value.variance ? (
                <span
                  className={cn(
                    'block text-caption',
                    value.variance < 0 ? 'text-state-danger' : 'text-ember-300',
                  )}
                >
                  {t('admin.cashSessions.variance', { amount: money(value.variance) })}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SessionDetail({
  eventId,
  report,
  money,
}: {
  eventId: string | undefined
  report: SessionReport
  money: (minor: number) => string
}) {
  const { t, i18n } = useTranslation()
  const movements = useAdminQuery<CashMovement[]>(
    ['pos-session-movements', report.session_id],
    `/ops/pos/sessions/${report.session_id}/movements`,
    eventId,
  )

  return (
    <div className="border-t border-line-subtle px-4 py-4">
      {report.station ? (
        <Badge tone="neutral">{t(`admin.registers.station.${report.station}`)}</Badge>
      ) : null}
      <ZReport report={report} money={money} className="mt-3" />

      {movements.data && movements.data.length > 0 ? (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-content-secondary">
            {t('admin.cashSessions.movements', { count: movements.data.length })}
          </summary>
          <ul className="mt-2 divide-y divide-line-subtle text-sm">
            {movements.data.map((row) => (
              <li key={row.id} className="flex items-baseline justify-between gap-3 py-1.5">
                <span className="min-w-0">
                  <span className="text-content-primary">{t(`pos.drawer.kinds.${row.kind}`)}</span>
                  {row.note ? (
                    <span className="block truncate text-caption text-content-muted">
                      {row.note}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-right">
                  <span
                    className={cn(
                      'block tabular-nums',
                      row.amount_minor < 0 ? 'text-state-danger' : 'text-content-primary',
                    )}
                  >
                    {money(row.amount_minor)}
                  </span>
                  <span className="block text-caption text-content-muted">
                    {new Date(row.created_at).toLocaleTimeString(i18n.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {report.status === 'open' ? (
        <RemoteClose eventId={eventId} report={report} money={money} />
      ) : null}
    </div>
  )
}

/**
 * Fermer la caisse de quelqu'un d'autre.
 *
 * Le compté d'abord, le théorique ensuite — comme au poste de vente : annoncer
 * ce qu'on devrait trouver invite à le recopier plutôt qu'à compter. Le motif
 * est toujours exigé : on doit pouvoir dire pourquoi ce n'est pas le
 * titulaire qui a compté.
 */
function RemoteClose({
  eventId,
  report,
  money,
}: {
  eventId: string | undefined
  report: SessionReport
  money: (minor: number) => string
}) {
  const { t } = useTranslation()
  const [counted, setCounted] = useState('')
  const [note, setNote] = useState('')
  const close = useAdminMutation<SessionReport, { counted_minor: number; note: string }>(
    'post',
    () => `/ops/pos/sessions/${report.session_id}/close`,
    eventId,
    [['pos-sessions-all', eventId ?? ''], ['admin-registers', eventId ?? '']],
  )

  const value = Number.parseFloat(counted.replace(',', '.'))
  const countedMinor = Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : null
  const variance = countedMinor === null ? null : countedMinor - report.expected_minor

  return (
    <div className="mt-5 rounded-lg border border-ember-400/30 p-4">
      <p className="text-sm text-content-primary">{t('admin.cashSessions.remoteTitle')}</p>
      <p className="mt-1 text-caption text-content-muted">{t('admin.cashSessions.remoteHint')}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[10rem_1fr]">
        <label className="block text-caption text-content-muted">
          {t('pos.counted')}
          <input
            inputMode="decimal"
            value={counted}
            onChange={(event) => setCounted(event.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
          />
        </label>
        <label className="block text-caption text-content-muted">
          {t('admin.cashSessions.remoteNote')}
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary"
          />
        </label>
      </div>
      {variance !== null && variance !== 0 ? (
        <p className="mt-2 text-sm text-ember-300">
          {t('pos.variance', { amount: money(variance) })}
        </p>
      ) : null}
      {close.error ? <p className="mt-2 text-sm text-state-danger">{close.error.message}</p> : null}
      <Button
        size="sm"
        className="mt-3"
        disabled={close.isPending || countedMinor === null || note.trim().length < 3}
        onClick={() =>
          countedMinor !== null && close.mutate({ counted_minor: countedMinor, note: note.trim() })
        }
      >
        {t('admin.cashSessions.remoteConfirm')}
      </Button>
    </div>
  )
}
